import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// import utility file
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import BulletinpaieService from '../../services/BulletinpaieService';
import BulletinpaieProfileLayout from './BulletinpaieProfileLayout';

const ViewBulletinpaie = () => {
	const { id } = useParams();
	const [bulletinpaie, setBulletinpaie] = useState(null);
	const [personnelBulletin, setPersonnelBulletin] = useState(null);
	const [contratBulletin, setcontratBulletin] = useState(null);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');

	useEffect(() => {
		Promise.all([BulletinpaieService.getBulletinpaie(id)]).then(
			([bulletinpaieData]) => {
				setBulletinpaie(bulletinpaieData.data);
				setPersonnelBulletin(bulletinpaieData.data.personnel);
				setcontratBulletin(bulletinpaieData.data.contrat);
			}
		);
	}, [id]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const bulletinpaieData = new FormData();

				// Add all your form data to the FormData
				bulletinpaieData.append('date_paiement', form.date_paiement.value);
				bulletinpaieData.append(
					'heures_supplementaires',
					form.heures_supplementaires.value
				);
				bulletinpaieData.append(
					'taux_heures_supplementaires',
					form.taux_heures_supplementaires.value
				);

				bulletinpaieData.append('indemnites', form.indemnites.value);
				bulletinpaieData.append('retenues', form.retenues.value);
				bulletinpaieData.append('personnel', personnelBulletin);
				bulletinpaieData.append('contrat', contratBulletin);

				// Call the BulletinpaieService to create the bulletinpaie
				await BulletinpaieService.updateBulletinpaie(id, bulletinpaieData);
				// Reset the form and state variables after successful creation
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Redirect the user to the list of bulletinpaies
				window.location.reload();
			} catch (error) {
				console.error(
					'Erreur lors de la mise à jour des informations du bulletin de paie',
					error
				);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<BulletinpaieProfileLayout>
			{bulletinpaie && (
				<>
					<Card className="mb-3 ">
						{/* Card body */}
						<Card.Body>
							{/*  Form  */}
							<Form
								className="mb-3"
								noValidate
								validated={validated}
								onSubmit={handleSubmit}
							>
								{showAlert && (
									<Alert variant="danger">
										<ExclamationTriangleFill size={25} className="me-1" />
										{alertMessage}
									</Alert>
								)}
								<Row>
									{/* Date paiement */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="date_paiement">
												Date paiement <span className="text-danger">*</span>
											</Form.Label>
											<Form.Control
												name="date_paiement"
												type="date"
												placeholder="Selectionner la date de paiement"
												id="date_paiement"
												required
												isInvalid={!!feedbackMessages['date_paiement']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														date_paiement: e.target.value
															? ''
															: 'La date de paiement de ce salaire est requise.'
													}));
												}}
												defaultValue={bulletinpaie.date_paiement || ''}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['date_paiement']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* Taux heures supplementaires et Heures supplementaires  */}
									<Col xs={12} className="mb-3">
										<Form.Group>
											<Row>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="taux_heures_supplementaires">
														Taux heures supplementaires{' '}
														<span className="text-danger">*</span>
													</Form.Label>
													<Form.Control
														name="taux_heures_supplementaires"
														type="number"
														placeholder="123456789 FCFA"
														id="taux_heures_supplementaires"
														required
														isInvalid={
															!!feedbackMessages['taux_heures_supplementaires']
														}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																taux_heures_supplementaires: e.target.value
																	? ''
																	: "Le taux d'heures supplementaires est requis."
															}));
														}}
														defaultValue={
															bulletinpaie.taux_heures_supplementaires || ''
														}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['taux_heures_supplementaires']}
													</Form.Control.Feedback>
												</Col>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="heures_supplementaires">
														Heures supplementaires
													</Form.Label>
													<Form.Control
														name="heures_supplementaires"
														type="number"
														min="0"
														defaultValue={
															bulletinpaie.heures_supplementaires || ''
														}
														placeholder="Heures suppléméntaires"
														id="heures_supplementaires"
														isInvalid={
															!!feedbackMessages['heures_supplementaires']
														}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																heures_supplementaires: e.target.value
																	? ''
																	: "La valeur sasie n'est pas valide."
															}));
														}}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['heures_supplementaires']}
													</Form.Control.Feedback>
												</Col>
											</Row>
										</Form.Group>
									</Col>

									{/* Indemnités */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="indemnites">Indemnités</Form.Label>
											<Form.Control
												name="indemnites"
												type="number"
												defaultValue={bulletinpaie.indemnites || ''}
												placeholder="123456789 FCFA"
												id="indemnites"
												isInvalid={!!feedbackMessages['indemnites']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														indemnites: e.target.value
															? ''
															: "La valeur des indemnités n'est pas valide."
													}));
												}}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['indemnites']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* Retenues */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="retenues">Retenues</Form.Label>
											<Form.Control
												name="retenues"
												type="number"
												defaultValue={bulletinpaie.retenues || ''}
												placeholder="123456789 FCFA"
												id="retenues"
												isInvalid={!!feedbackMessages['retenues']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														retenues: e.target.value
															? ''
															: "La valeur des retenues n'est pas valide."
													}));
												}}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['retenues']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* Buttons */}
									<Col xs={12} className="mt-4">
										<Button variant="success" type="submit">
											Mettre à jour
										</Button>
										<Button
											type="reset"
											variant="outline-danger"
											className="ms-2"
										>
											Effacer
										</Button>
									</Col>
								</Row>
							</Form>
						</Card.Body>
					</Card>
				</>
			)}
		</BulletinpaieProfileLayout>
	);
};

export default ViewBulletinpaie;
